import React from 'react';
import { Box, Button, Typography, Alert, CircularProgress, Modal } from '@mui/material';

interface LockCardModalProps {
  onClose: () => void;
  onConfirm: () => void;
  cardLast4?: string;
  error?: string | null;
  isLoading?: boolean;
}

function LockCardModal({ onClose, onConfirm, cardLast4, error, isLoading }: LockCardModalProps): JSX.Element {
  return (
    <Modal
      open={true}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(5px)',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          pt: '20vh',
          height: '100vh',
          width: '100vw',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            py: 2, // Vertical padding only
            width: '85%',
            maxWidth: 400,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: 'text.primary',
              p: 1,
            }}
          >
            Card Lock
          </Typography>
          <Typography
            variant="large"
            sx={{
              textAlign: 'center',
              color: '#000',
              fontWeight: 300,
              lineHeight: 1.5,
              whiteSpace: 'pre-line',
              p: 1,
            }}
          >
            {`Locking the card ending in ${
              cardLast4 || '****'
            } will temporarily restrict you from making any purchases. You can always unlock it later.

Would you like to proceed?`}
          </Typography>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              px: 2.5,
              py: 1,
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={onConfirm}
              disabled={isLoading}
              sx={{
                py: 2.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.5rem',
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Locking...
                </>
              ) : (
                'Yes'
              )}
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={onClose}
              disabled={isLoading}
              sx={{
                py: 2.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.5rem',
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
              }}
            >
              {error ? 'Try Again' : 'Back'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

export default LockCardModal;
